import React from 'react'
import { CashBalanceTransactionSearchForm } from 'types/interfaces'
import { useAPI } from 'contexts/APIProvider'
import CashBalanceTransaction from 'logic/CashBalanceTransasction'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { GridColDef, GridRenderCellParams, GridValueFormatterParams } from '@mui/x-data-grid'
import { GridValueGetterParams } from '@mui/x-data-grid-pro'
import { Box, Button, Typography, Alert, Link } from '@mui/material'
import FormField from 'components/FormField'
import Loading from 'components/Loading'
import { isEmpty } from 'lodash'
import { DataGridPro } from '@mui/x-data-grid-pro'
import ExportOnlyToolBar from 'components/ExportToolbar'
import { Link as RouterLink } from 'react-router-dom'
import { adminBillingAccountDetailURL } from 'routes/urls'

type CashBalanceTransactionsTableProps = {
    form: CashBalanceTransactionSearchForm
    updateForm: (name: string, value: any) => void
}

const CashBalanceTransactionsTable: React.FC<CashBalanceTransactionsTableProps> = ({ form, updateForm }) => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const [transactions, setTransactions] = React.useState<CashBalanceTransaction[]>([])
    const [loading, setLoading] = React.useState<boolean>(false)    

    const fetchTransactions = React.useCallback(async () => {
        try {
            setLoading(true)
            const result = await api.listCashBalanceTransactions(form)
            setTransactions(result)
        } catch (error) {
            showAlert('error', 'Failed to fetch transactions')
        } finally {
            setLoading(false)
        }
    }, [form, api, showAlert])

    const columns: GridColDef<CashBalanceTransaction>[] = React.useMemo(() => [{
        minWidth: 150,
        headerName: 'Date',
        field: 'transaction_created_at',
        type: 'dateTime',
        valueGetter: (params: GridValueGetterParams<CashBalanceTransaction>) => {
            return params.row.transaction_created_at?.toJSDate()
        },
        renderCell: (params: GridRenderCellParams<CashBalanceTransaction>) => {
            return params.row.transaction_date_label()
        }
    }, {
        minWidth: 200,
        headerName: 'Practice Group',
        field: 'practice_group_label',
        type: 'string',
    }, {
        minWidth: 200,
        headerName: 'Billing Account',
        field: 'billing_account_label',
        type: 'string',
        renderCell: (params: GridRenderCellParams<CashBalanceTransaction>) => {
            return (
                <Link component={RouterLink} to={adminBillingAccountDetailURL(params.row.billing_account_id)}>
                    { params.row.billing_account_label }
                </Link>
            )
        }
    }, {
        minWidth: 200,
        headerName: 'Type',
        field: 'transaction_type',
        type: 'string',
    }, {
        minWidth: 200,
        headerName: 'Reference',
        field: 'reference',
        type: 'string',
    }, {
        minWidth: 200,
        headerName: 'Applied to Invoice',
        field: 'invoice_number',
        type: 'string',
    }, {
        minWidth: 200,
        headerName: 'Amount',
        field: 'amount',
        type: 'number',
        valueFormatter: (params: GridValueFormatterParams<number>) => {
            return params.value / 100
        },
        renderCell: (params: GridRenderCellParams<CashBalanceTransaction>) => {
            return params.row.amount_label
        },
    }, {
        minWidth: 200,
        headerName: 'Ending Balance',
        field: 'ending_balance',
        type: 'number',
        valueFormatter: (params: GridValueFormatterParams<number>) => {
            return params.value / 100
        },
        renderCell: (params: GridRenderCellParams<CashBalanceTransaction>) => {
            return params.row.ending_balance_label
        }
    }, {
        minWidth: 200,
        headerName: 'Sender',
        field: 'sender_name',
        type: 'string',
    }, {
        minWidth: 200,
        headerName: 'Bank Account (FROM)',
        field: 'sender_account_last4',
        type: 'string',
        renderCell: (params: GridRenderCellParams<CashBalanceTransaction>) => {
            return (
                <Box sx={{'display': 'flex', 'flexDirection': 'column'}}>
                    <Typography variant='caption'>Account: ending in {params.row.sender_account_last4}</Typography>
                    <Typography variant='caption'>Sort Code: {params.row.sender_sort_code}</Typography>
                </Box>
            )
        }
    }, {
        minWidth: 200,
        headerName: 'Bank Account (TO)',
        field: 'account_number',
        type: 'string',
        renderCell: (params: GridRenderCellParams<CashBalanceTransaction>) => {
            return (
                <Box sx={{'display': 'flex', 'flexDirection': 'column'}}>
                    <Typography variant='caption'>Account: {params.row.account_number}</Typography>
                    <Typography variant='caption'>Sort Code: {params.row.sort_code}</Typography>
                </Box>
            )
        }
    }], [])
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <FormField
              name='Text Search'
              label='Text Search'
              sx={{ width: 300 }}
              onChange={(e) => updateForm('search_term', e.target.value)}
              value={form.search_term ?? ''}>
            </FormField>
            <Button
              variant="contained"
              onClick={fetchTransactions}
              disabled={loading}>
              {loading ? 'Please Wait ...' : 'Search'}
            </Button>
            <Typography variant='caption' color='error'>
              Records won't load until you click the Search button
            </Typography>
          </Box>
          {loading ? (
            <Loading />
          ) : isEmpty(transactions) ? (
            <Alert severity='info'>No Records Found</Alert>
          ) : (
            <React.Fragment>
              <Alert severity='info'>{transactions.length} records returned</Alert>
              <DataGridPro 
                rows={transactions}
                columns={columns}
                getRowId={(transaction) => transaction.transaction_id}
                loading={loading}
                hideFooter
                slots={{ toolbar: ExportOnlyToolBar }}
              />
            </React.Fragment>
          )}
        </Box>
    )
}

export default CashBalanceTransactionsTable